import {useState} from 'react';

import {type LogLineData} from '../LogLineRenderer';
import {useLogLineHeightEstimates} from './useLogLineHeightEstimates';

interface UseLogListScrollerArgs {
  // forwarded from the body's useAutoScroll so its scroll-distance logic sees
  // the scroller element too.
  handleScrollerRef: (ref: HTMLElement | Window | null) => void;
  lineHeight: number;
  lines: readonly LogLineData[];
  maxLabelLength: number;
  maxLineNumLength: number;
  showLabel: boolean;
  showLineNumber: boolean;
  showTimestamp: boolean;
}

interface UseLogListScrollerResult {
  measurer: React.ReactElement;
  heightEstimates: number[] | null;
  setScrollerRef: (ref: HTMLElement | Window | null) => void;
}

/**
 * shared scroller plumbing for the log list bodies: tracks the virtuoso
 * scroller element (forwarding it to useAutoScroll) and derives per-line height
 * estimates from it. both DenseLogList and GlobalLogList wire the returned
 * setScrollerRef to their `<Virtuoso scrollerRef>` — keeping this in one place
 * so the estimates can't silently go unwired in one body.
 */
export function useLogListScroller({
  handleScrollerRef,
  lineHeight,
  lines,
  maxLabelLength,
  maxLineNumLength,
  showLabel,
  showLineNumber,
  showTimestamp,
}: UseLogListScrollerArgs): UseLogListScrollerResult {
  const [scrollerElement, setScrollerElement] = useState<HTMLDivElement | null>(
    null
  );

  const setScrollerRef = (ref: HTMLElement | Window | null) => {
    handleScrollerRef(ref);
    setScrollerElement(ref instanceof HTMLDivElement ? ref : null);
  };

  const {heightEstimates, measurer} = useLogLineHeightEstimates({
    scrollerElement,
    lineHeight,
    lines,
    maxLabelLength,
    maxLineNumLength,
    showLabel,
    showLineNumber,
    showTimestamp,
  });

  return {measurer, heightEstimates, setScrollerRef};
}
